/** @format */

import type React from "react";
import { type FC, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDiscardModal } from "../../../hooks";
import { cn } from "../../../util/classNameConstructor";
import StaticMap from "../../atoms/map/StaticMap";
import Button from "../../core/button/Button";
import Dropdown from "../../core/dropdown/Dropdown";
import TruncateMiddle from "../../core/TruncateMiddle";

export interface MapCardData {
  id?: string;
  mapId?: string;
  title?: string;
  imgURL?: string;
  mapbounds?: {
    _northEast: { lat: number; lng: number };
    _southWest: { lat: number; lng: number };
  };
}

export interface MapCardProps {
  data?: MapCardData;
  title?: string;
  subTitle?: string;
  img?: string;
  onClick?: (data: MapCardData) => void;
  onEdit?: (data: MapCardData) => void;
  onDuplicate?: (data: MapCardData) => void;
  onDelete?: (data: MapCardData) => void;
  selected?: string;
}

const MapCard: FC<MapCardProps> = ({
  data,
  onClick,
  onEdit,
  onDuplicate,
  onDelete,
  duplicatable,
  selected,
}) => {
  const { t } = useTranslation();
  const { displayDiscardModal } = useDiscardModal();

  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleClick = () => onClick?.(data as MapCardData);
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      handleClick();
    }
  };

  const [ready, setReady] = useState(false);
  useEffect(() => {
    setReady(true);
  }, []);

  return (
    <div
      className={cn(
        "relative duration-1000 ease-expressive-out aspect-square",
        ready ? "opacity-100 translate-y-0" : "opacity-0 translate-y-20",
      )}
    >
      <div
        type="button"
        tabIndex={0}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        className={cn(
          "grid grid-rows-[1fr_auto] h-full",
          "cursor-pointer rounded-xl bg-white border-border-default hover:bg-action-secondary-hovered hover:border-border-hovered active:bg-action-secondary-pressed active:border-border-pressed border border-solid",
          selected &&
            (selected === data?.id || selected === data?.mapId) &&
            "border-2 border-border-highlight-default bg-surface-highlight-subdued",
        )}
      >
        <div className="relative box-border w-full overflow-hidden rounded-t-[11px] border-b border-border-default bg-white">
          {data?.thumbnail && (
            <img
              src={data.thumbnail}
              alt={data?.title}
              className="w-full h-full object-cover bg-gray-50"
            />
          )}

          {!data?.thumbnail && data?.mapbounds && (
            <StaticMap mapbounds={data.mapbounds} key={data?.mapId} />
          )}
        </div>

        <div className="relative my-2 px-4 h-6 min-w-0">
          <TruncateMiddle
            text={data?.title || t("global:unnamed")}
            tag="h3"
            className="body-md"
            title={data?.title || t("global:unnamed")}
          />
        </div>
      </div>
      <div ref={dropdownRef} className="absolute end-1 top-1">
        <Dropdown
          variant="outline"
          className="shadow-lg bg-white"
          trailingIcon="more"
          portalContainer={
            document.getElementById("map-datasets-list") || undefined
          }
          onClick={(e) => {
            e.stopPropagation();
          }}
          closeOnClick={true}
          items={[
            {
              leadingIcon: "edit",
              text: t("util:edit", { val: t("global:map") }),
              size: "md",
              onClick: (e) => {
                e.preventDefault();
                e.stopPropagation();
                onEdit?.(data as MapCardData);
              },
            },
            {
              leadingIcon: "duplicate",
              text: t("util:duplicate", { val: t("global:map") }),
              size: "md",
              disabled: !duplicatable,
              disabledNotice: t("global:maxMapsReached"),
              onClick: (e) => {
                e.stopPropagation();
                onDuplicate?.(data as MapCardData);
              },
            },
            {
              leadingIcon: "trash",
              text: t("util:delete", { val: t("global:map") }),
              size: "md",
              critical: true,
              onClick: (e) => {
                e.stopPropagation();
                displayDiscardModal({
                  title: t("util:delete", { val: t("global:map") }),
                  description: t("global:deleteDescription"),
                  submitText: t("global:delete"),
                  onSubmit: async () => {
                    onDelete?.(data as MapCardData);
                  },
                });
              },
            },
          ]}
        />
      </div>
    </div>
  );
};

export default MapCard;
